import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoIosArrowBack } from 'react-icons/io';
import { Col, Row } from 'react-bootstrap';
import ExcelJS from 'exceljs';
import TodaysWorkStatOfSelectedProject from './TodaysWorkStatOfSelectedProject';
import MonthsWorkStatOfSelectedProject from './MonthsWorkStatOfSelectedProject';
import OvertimeWorkStatOfSelectedProject from './OvertimeWorkStatOfSelectedProject';
import TotalWorkStatOfSelectedProject from './TotalWorkStatOfSelectedProject';
import axiosInstance from '../../../utils/axiosInstance';
import format from 'date-fns/format';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../../utils/timeUtils';
import WorkDataDateRangeFilter from '../../common/WorkDataDateRangeFilter';
import {
    filterWorkDetailsByDateRange,
    getLocalCalendarMonthBounds,
    resolveWorkFetchDateRange,
} from '../../../utils/workDataDateRange';

const ProjectDetailsByEmployee = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { employeeId, employeeName, projectId, projectName } = location.state || {};
    const [employeeWorkDetails, setEmployeeWorkDetails] = useState([]);
    const [totalWorkData, setTotalWorkData] = useState({ startDate: '', endDate: '', totalDuration: '00:00:00' });
    const [totalTodaysWorkData, setTotalTodaysWorkData] = useState('00:00:00');
    const [monthlyWorkDuration, setMonthlyWorkDuration] = useState('00:00:00');
    const [totalOvertimeData, setTotalOvertimeData] = useState({ startDate: '', endDate: '', totalOvertimeDuration: '00:00:00' });
    const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [workRangeStart, setWorkRangeStart] = useState(() => getLocalCalendarMonthBounds().startDate);
    const [workRangeEnd, setWorkRangeEnd] = useState(() => getLocalCalendarMonthBounds().endDate);

    const rangeQueryParams = useMemo(
        () => resolveWorkFetchDateRange(workRangeStart, workRangeEnd),
        [workRangeStart, workRangeEnd]
    );

    const displayWorkDetails = useMemo(
        () => filterWorkDetailsByDateRange(employeeWorkDetails, workRangeStart, workRangeEnd),
        [employeeWorkDetails, workRangeStart, workRangeEnd]
    );

    const monthNow = getLocalCalendarMonthBounds();
    const showResetToCurrentMonth =
        workRangeStart !== monthNow.startDate || workRangeEnd !== monthNow.endDate;

    useEffect(() => {
        const detectTimezone = () => {
            const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (detectedZone !== userTimeZone) {
                setUserTimeZone(detectedZone);
            }
        };

        window.addEventListener('focus', detectTimezone);
        return () => {
            window.removeEventListener('focus', detectTimezone);
        };
    }, [userTimeZone]);

    const onBack = () => {
        navigate(`/projects-by-employee/${employeeId}`, {
            state: { employeeId, employeeName }
        });
    };

    const onTotalWorkData = (data) => {
        setTotalWorkData(data);
    };

    const onTotalOvertimeData = (info) => {
        setTotalOvertimeData(info);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';

            const options = {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                timeZone: userTimeZone
            };
            return date.toLocaleDateString(undefined, options);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Format timezone for display
    const formatTimeZone = () => {
        try {
            const timeZoneAbbr = new Intl.DateTimeFormat('en', {
                timeZoneName: 'short',
                timeZone: userTimeZone
            }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

            return timeZoneAbbr || userTimeZone.split('/').pop();
        } catch (e) {
            return userTimeZone.split('/').pop();
        }
    };

    const fetchEmployeeWorkDetails = async () => {
        try {
            const response = await axiosInstance.get(
                `/admin/ProjectWorkDetailsByEmployee-display-all-data/${employeeId}/${projectId}`,
                { params: rangeQueryParams }
            );
            const normalizedData = response.data.map(detail => ({
                ...detail,
                time_start: normalizeDbTimestampToUtcIso(detail.time_start, detail.work_date),
                time_stop: normalizeDbTimestampToUtcIso(detail.time_stop, detail.work_date)
            }));
            setEmployeeWorkDetails(normalizedData);
        } catch (error) {
            console.error('Error fetching employee work details:', error.message);
        }
    };

    const fetchTodaysWorkDuration = async () => {
        try {
            const response = await axiosInstance.get(`/admin/ProjectDetailsByEmployee-work-duration/${employeeId}/${projectId}`);
            setTotalTodaysWorkData(response.data.totalDuration);
        } catch (error) {
            console.error('Error fetching total work duration:', error.message);
        }
    };

    const fetchMonthlyWorkDuration = async () => {
        try {
            const response = await axiosInstance.get(`/admin/ProjectDetailsByEmployee-monthly-work-duration/${employeeId}/${projectId}`);
            setMonthlyWorkDuration(response.data.totalWorkDuration);
        } catch (error) {
            console.error('Error fetching monthly work duration:', error.message);
        }
    };

    const fetchTotalWorkDuration = async () => {
        try {
            const response = await axiosInstance.get(
                `/admin/ProjectDetailsByEmployee-total-work-duration/${employeeId}/${projectId}`,
                { params: rangeQueryParams }
            );
            const { startDate, endDate, totalDuration } = response.data;
            onTotalWorkData({
                startDate: startDate ?? '',
                endDate: endDate ?? '',
                totalDuration
            });
        } catch (error) {
            console.error('Error fetching total work duration:', error.message);
        }
    };

    const fetchOvertimeDuration = async () => {
        try {
            const response = await axiosInstance.get(
                `/admin/ProjectDetailsByEmployee-overtime-duration/${employeeId}/${projectId}`,
                { params: rangeQueryParams }
            );
            const { startDate, endDate, totalOvertimeDuration } = response.data;
            onTotalOvertimeData({
                startDate: startDate ?? '',
                endDate: endDate ?? '',
                totalOvertimeDuration
            });
        } catch (error) {
            console.error('Error fetching overtime duration:', error.message);
        }
    };

    useEffect(() => {
        if (!employeeId || !projectId) return;

        fetchEmployeeWorkDetails();
        fetchTodaysWorkDuration();
        fetchMonthlyWorkDuration();
        fetchTotalWorkDuration();
        fetchOvertimeDuration();
    }, [employeeId, projectId, rangeQueryParams.startDate, rangeQueryParams.endDate]);

    const downloadExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(`${employeeName}'s Work Data`);

            sheet.views = [{ showGridLines: false }];
            sheet.addRow([]);

            // Title
            sheet.mergeCells('B1:G1');
            sheet.getCell('B1').value = `${employeeName}'s Work Data of ${projectName}`;
            sheet.getCell('B1').font = { size: 18, bold: true };
            sheet.getCell('B1').alignment = { vertical: 'middle', horizontal: 'center' };
            sheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };

            // Timezone info
            // sheet.mergeCells('B2:G2');
            // sheet.getCell('B2').value = `All times shown in ${formatTimeZone()}`;
            // sheet.getCell('B2').font = { size: 12, italic: true };
            // sheet.getCell('B2').alignment = { vertical: 'middle', horizontal: 'center' };

            // Summary Data Header
            sheet.addRow(['']);
            sheet.addRow(['', '', 'Todays Work', 'Months Work', 'Total Work', 'Total Overtime', '']);
            const summaryHeaderRow = sheet.getRow(3);
            summaryHeaderRow.font = { bold: true };
            summaryHeaderRow.alignment = { vertical: 'middle', horizontal: 'center' };

            const summaryToday = totalTodaysWorkData || '-';
            const summaryMonth = monthlyWorkDuration || '-';
            const summaryTotal = totalWorkData.totalDuration || '-';
            const summaryOt = totalOvertimeData.totalOvertimeDuration || '-';

            sheet.addRow([
                '',
                '',
                summaryToday,
                summaryMonth,
                summaryTotal,
                summaryOt,
                '',
            ]);

            const totalRangeLabel = `${formatDate(totalWorkData.startDate)} to ${formatDate(totalWorkData.endDate)}`;
            const otRangeLabel = `${formatDate(totalOvertimeData.startDate)} to ${formatDate(totalOvertimeData.endDate)}`;

            sheet.addRow([
                '',
                '',
                formatDate(new Date()),
                new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
                totalRangeLabel,
                otRangeLabel,
                '',
            ]);

            sheet.addRow(['']); // Blank row
            sheet.mergeCells('B7:G7');
            sheet.getCell('B7').value = t("employeeWorkDetails_workTitle");
            sheet.getCell('B7').alignment = { vertical: 'middle', horizontal: 'center' };

            const legendRow = sheet.addRow(['', '', '', t("employeeWorkDetails_overtime"), t("Regular")]);
            legendRow.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'fcdada' } };
            legendRow.getCell(4).alignment = { vertical: 'middle', horizontal: 'center' };
            legendRow.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'deffd6' } };
            legendRow.getCell(5).alignment = { vertical: 'middle', horizontal: 'center' };

            sheet.addRow(['']);

            // Add Work Details Header
            const headers = [
                '',
                t('employeeWorkDetails_SrNo'),
                t('employeeWorkDetails_date'),
                t('employeeWorkDetails_startTime'),
                t('employeeWorkDetails_EndTime'),
                t('employeeWorkDetails_duration'),
                t('employeeWorkDetails_comment'),
            ];
            const headerRow = sheet.addRow(headers);
            headerRow.eachCell((cell, colNumber) => {
                if (colNumber !== 1) {
                    cell.font = { bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };
                }
            });

            // Add Work Details Data
            displayWorkDetails.forEach((detail, index) => {
                const row = sheet.addRow([
                    '',
                    index + 1,
                    format(new Date(detail.work_date), 'dd/MM/yyyy'),
                    formatISOToLocalTime(detail.time_start),
                    detail.time_stop ? formatISOToLocalTime(detail.time_stop) : '-',
                    detail.work_duration || '-',
                    detail.comments || '-',
                ]);

                row.eachCell((cell, colNumber) => {
                    if (detail.overtime === 'Overtime' && colNumber !== 1 && colNumber !== 8) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'fcdada' } };
                    } else if (colNumber !== 1 && colNumber !== 8) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'deffd6' } };
                    }
                    if (colNumber !== 7) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }
                });
            });

            // Set column widths
            sheet.columns.forEach((column, colNumber) => {
                column.width = colNumber === sheet.columns.length ? 5 : 40;
            });

            // Save the Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${employeeName}_Work_Data_${projectName}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating Excel file:', error);
        }
    };

    return (
        <div className='ManageProjectsByEmployees-container'>
            <div className='button-section'>
                <button className="backButton" onClick={onBack}>
                    <IoIosArrowBack />Back
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <button className="downloadButton" onClick={downloadExcel}>Download</button>
                </div>
            </div>

            <h2 className=''>{employeeName}'s Work Data of {projectName}</h2>
            {/* <div className='timezone-info'>
                ({t("Times shown in")} {formatTimeZone()})
            </div> */}
            <div className='d-flex justify-content-end'>
                <WorkDataDateRangeFilter
                    startDate={workRangeStart}
                    endDate={workRangeEnd}
                    onStartDateChange={setWorkRangeStart}
                    onEndDateChange={setWorkRangeEnd}
                    showClearButton={showResetToCurrentMonth}
                    labels={{ clear: 'This month' }}
                    onClear={() => {
                        const b = getLocalCalendarMonthBounds();
                        setWorkRangeStart(b.startDate);
                        setWorkRangeEnd(b.endDate);
                    }}
                />
            </div>

            <div className='Empstats'>
                <Row>
                    <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                        <TodaysWorkStatOfSelectedProject totalDuration={totalTodaysWorkData} />
                    </Col>
                    <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                        <MonthsWorkStatOfSelectedProject totalDuration={monthlyWorkDuration} />
                    </Col>
                    <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                        <TotalWorkStatOfSelectedProject
                            startDate={totalWorkData.startDate}
                            endDate={totalWorkData.endDate}
                            totalDuration={totalWorkData.totalDuration}
                        />
                    </Col>
                    <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                        <OvertimeWorkStatOfSelectedProject
                            startDate={totalOvertimeData.startDate}
                            endDate={totalOvertimeData.endDate}
                            totalDuration={totalOvertimeData.totalOvertimeDuration}
                        />
                    </Col>
                </Row>
            </div>
            <div className='legend-body'>
                <p className='TableTitle'>{t("employeeWorkDetails_workTitle")}</p>
                <div className="legend">
                    <div className="legend-item">
                        <span className="legend-color overtime-color"></span>
                        <span>{t("employeeWorkDetails_overtime")}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color regular-color"></span>
                        <span>{t("Regular")}</span>
                    </div>
                </div>
            </div>

            <div className="ManageProjectsByEmployees-table-section">
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ marginTop: '30px', minWidth: '800px' }}>
                        <thead>
                            <tr>
                                <th>{t("employeeWorkDetails_SrNo")}</th>
                                <th>{t("employeeWorkDetails_date")}</th>
                                <th>{t("employeeWorkDetails_startTime")}</th>
                                <th>{t("employeeWorkDetails_EndTime")}</th>
                                <th>{t("employeeWorkDetails_duration")}</th>
                                <th>{t("employeeWorkDetails_comment")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayWorkDetails && displayWorkDetails.length > 0 ? (
                                displayWorkDetails.map((detail, index) => (
                                    <tr key={index} className={detail.overtime === 'Overtime' ? 'overtime-row' : 'rugular-row'}>
                                        <td>{index + 1}</td>
                                        <td>{format(new Date(detail.work_date), 'dd/MM/yyyy')}</td>
                                        <td>{formatISOToLocalTime(detail.time_start)}</td>
                                        <td>{detail.time_stop ? formatISOToLocalTime(detail.time_stop) : '-'}</td>
                                        <td>{detail.work_duration || '-'}</td>
                                        <td>
                                            <span className="comment" title={detail.comments}>
                                                {detail.comments && detail.comments.length > 20
                                                    ? `${detail.comments.slice(0, 20)}...`
                                                    : detail.comments || '-'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : employeeWorkDetails.length > 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', color: 'lightgray', opacity: '0.6' }}>
                                        No contributions in this date range
                                    </td>
                                </tr>
                            ) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', color: 'lightgray', opacity: '0.6' }}>
                                        No contributions yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ProjectDetailsByEmployee